import {
  languageSchema,
  unitLengthSchema,
  unitPaceSchema,
  unitSlopeSchema,
  unitTemperatureSchema,
} from "@mountaya/studio";
import type { FrontendApi } from "@ory/client-fetch";
import { isResponseError } from "@ory/client-fetch";
import { z } from "zod";

/**
 * preferencesSchema mirrors the backend Preferences shape: the i18n language and the four
 * unit choices.
 *
 * The five vocabularies are the SDK's, which pins each of them to the wire enum the backend
 * publishes. Restating them here would be a second place for the same five lists to be right,
 * and a `.catch(undefined)` makes a wrong one silent: an unrecognised value resolves to
 * undefined and the consumer falls back to its default, so a vocabulary that fell behind
 * reads as a user whose preference stopped sticking rather than as a parse error.
 *
 * The OBJECT is this platform's own session contract and is composed twice — here and in the
 * identity store — because the two decode different payloads into the same value. Each field
 * is optional and `.catch(undefined)` so a sparse `metadata_public` resolves rather than
 * failing the whole session parse, and `sessionContract.test.ts` pins that the two
 * compositions parse identically. The app renders light-only, so the backend's theme choice is
 * not surfaced as a preference; Zod drops the unknown key.
 */
export const preferencesSchema = z.object({
  language: languageSchema.optional().catch(undefined),
  length: unitLengthSchema.optional().catch(undefined),
  pace: unitPaceSchema.optional().catch(undefined),
  slope: unitSlopeSchema.optional().catch(undefined),
  temperature: unitTemperatureSchema.optional().catch(undefined),
});

/**
 * DisplayPreferences is the user's display preferences as the session carries them, derived
 * from preferencesSchema: the language and the four units, each optional, and no theme. It is
 * not the contract's Preferences, which the preferences endpoint answers with every field set.
 */
export type DisplayPreferences = z.infer<typeof preferencesSchema>;

/**
 * traitsSchema mirrors the Ory identity traits (email + names + newsletter
 * opt-in). These are the user-supplied, schema-validated traits Ory stores.
 *
 * Only `email` is load-bearing — it identifies the account and keys the
 * email-verified derivation — so it alone is required. The rest are display
 * values, each `.catch`-defaulted: an identity created through a path that
 * populates fewer traits (an OIDC signup, a staff-provisioned account) must still
 * resolve to a session. A session that fails to parse reads as ANONYMOUS to the
 * app while Ory keeps answering `session_already_available` to every flow init,
 * and that disagreement is a full-page redirect cycle — so parsing is deliberately
 * forgiving on everything that is not identity-critical.
 */
export const traitsSchema = z.object({
  email: z.string(),
  first_name: z.string().catch(""),
  last_name: z.string().catch(""),
  newsletter: z.boolean().catch(false),
});

/**
 * Traits are the Ory identity traits, derived from traitsSchema.
 */
export type Traits = z.infer<typeof traitsSchema>;

/**
 * verifiableAddressSchema parses one entry of Ory's identity.verifiable_addresses
 * (the address value, its channel, and whether it is verified). Only the fields
 * the email-verified derivation needs are modeled; Zod drops the rest.
 */
export const verifiableAddressSchema = z.object({
  value: z.string(),
  verified: z.boolean(),
  via: z.string(),
});

/**
 * VerifiableAddress is a single parsed verifiable address, derived from
 * verifiableAddressSchema.
 */
export type VerifiableAddress = z.infer<typeof verifiableAddressSchema>;

/**
 * DEFAULT_METADATA_PUBLIC is what an identity whose `metadata_public` is missing or
 * unreadable resolves to: no stated preferences, so every consumer falls back to its
 * own default. It grants nothing — what a person is entitled to is resolved from their
 * organization membership, which the lookup writes after the session lands, so a sparse
 * payload costs a reader their display preferences and nothing else.
 */
const DEFAULT_METADATA_PUBLIC = { preferences: {} } satisfies {
  preferences: DisplayPreferences;
};

/**
 * metadataPublicSchema parses Ory's untrusted metadata_public (typed `object`
 * in the SDK) into the app's User metadata. Parsing it (not casting the `object`)
 * honors the Zod-first boundary rule.
 *
 * The preferences are `.catch`-defaulted because the backend populates them through
 * a hook that can lag identity creation, so a freshly registered user reads them
 * sparse. Failing the parse there would report a signed-in user as anonymous while
 * Ory still holds their session — the exact disagreement that turns a visit to
 * /login into a redirect cycle. The fallback grants nothing (see
 * {@link DEFAULT_METADATA_PUBLIC}), so forgiving parsing never grants access.
 */
export const metadataPublicSchema = z.object({
  preferences: preferencesSchema.catch({}),
});

/**
 * MetadataPublic is the parsed metadata_public, derived from
 * metadataPublicSchema. It is the value object the app surfaces as `metadata`
 * (the display preferences; the id/traits are carried separately).
 */
export type MetadataPublic = z.infer<typeof metadataPublicSchema>;

/**
 * identitySessionSchema is the IdentitySession value object produced by the
 * session bootstrap: the user id, parsed traits, parsed metadata, and
 * emailVerified. emailVerified is true only when the identity carries a verified
 * verifiable address for the trait email; verification is non-blocking at signup,
 * so it is false right after registration and flips once the user verifies. The
 * verifiableAddresses input defaults to [] (`.catch`) so a sparse payload never
 * fails the whole session parse; `metadata` defaults the same way, covering the
 * identity whose metadata_public has not been written at all.
 */
export const identitySessionSchema = z
  .object({
    metadata: metadataPublicSchema.catch(DEFAULT_METADATA_PUBLIC),
    traits: traitsSchema,
    userId: z.string(),
    verifiableAddresses: z.array(verifiableAddressSchema).catch([]),
  })
  .transform((parsed) => ({
    emailVerified: parsed.verifiableAddresses.some(
      (address) =>
        address.via === "email" &&
        // Emails are case-insensitive; normalize both sides so a casing difference
        // between the trait and the address never reads a verified user as unverified.
        address.value.toLowerCase() === parsed.traits.email.toLowerCase() &&
        address.verified,
    ),
    metadata: parsed.metadata,
    preferences: parsed.metadata.preferences,
    traits: parsed.traits,
    userId: parsed.userId,
  }));

/**
 * IdentitySession is the Zod-parsed session value object the bootstrap returns;
 * app code writes it into the Identity store (the leaf never imports core).
 */
export type IdentitySession = z.infer<typeof identitySessionSchema>;

/**
 * bootstrapSession calls Ory's toSession (whoami) and parses the result into an
 * IdentitySession value object. Returns null when there is no active session (a
 * 401), so the caller treats the user as anonymous.
 *
 * A sparse payload does NOT fail: every non-identifying field carries a
 * least-privileged `.catch` default, because a parse failure would report a
 * signed-in user as anonymous while Ory keeps holding their session, and the app's
 * "anonymous" verdict then fights Ory's "already signed in" in a redirect cycle.
 * The parse still throws a ZodError on a genuinely unusable identity — one with no
 * trait email — since nothing downstream can name that user.
 *
 * Stays a leaf: it returns a plain value object; app code writes the Identity
 * store. credentials: "include" on the FrontendApi config sends the
 * ory_session_* cookie.
 */
export const bootstrapSession = async (client: FrontendApi): Promise<IdentitySession | null> => {
  try {
    const session = await client.toSession();
    const identity = session.identity;
    if (!identity) {
      return null;
    }

    return identitySessionSchema.parse({
      metadata: identity.metadata_public,
      traits: identity.traits,
      userId: identity.id,
      verifiableAddresses: identity.verifiable_addresses,
    });
  } catch (err) {
    if (isResponseError(err) && err.response.status === 401) {
      return null;
    }

    throw err;
  }
};
